type LogLevel = 'debug' | 'info' | 'warn' | 'error';

declare global {
  interface Window {
    __DICE_LOGS__?: boolean;
    __DICE_DEBUG__?: boolean;
  }
}

const isDevEnvironment = typeof import.meta !== 'undefined' && Boolean(import.meta.env?.DEV);

function computeVerboseFlag(): boolean {
  // Production build: never enable verbose/debug logs, even with runtime globals.
  if (!isDevEnvironment) {
    return false;
  }
  const w = typeof globalThis !== 'undefined' && 'window' in globalThis
    ? globalThis.window
    : undefined;
  if (w) {
    if (typeof w.__DICE_LOGS__ === 'boolean') {
      return w.__DICE_LOGS__;
    }
    if (typeof w.__DICE_DEBUG__ === 'boolean') {
      return w.__DICE_DEBUG__;
    }
  }
  return false;
}

let cachedVerbose: boolean | undefined;

export function isVerboseLoggingEnabled(): boolean {
  const w = typeof globalThis !== 'undefined' && 'window' in globalThis
    ? globalThis.window
    : undefined;
  if (w) {
    // Nie cache'ujemy w środowisku przeglądarkowym, żeby reagować na runtime toggles.
    return computeVerboseFlag();
  }
  if (cachedVerbose === undefined) {
    cachedVerbose = computeVerboseFlag();
  }
  return cachedVerbose;
}

function emit(level: LogLevel, tag: string, payload?: unknown, always = false): void {
  if (!always && !isVerboseLoggingEnabled()) {
    return;
  }

  const style = level === 'debug' || level === 'info'
    ? 'color:#9fdbff'
    : level === 'warn'
      ? 'color:#f8d17f'
      : 'color:#ffa1a1';

  const prefix = `%c[${tag}]`;
  const args = payload === undefined ? [prefix, style] : [prefix, style, payload];

  switch (level) {
    case 'debug':
      console.debug(...args);
      break;
    case 'info':
      console.info(...args);
      break;
    case 'warn':
      console.warn(...args);
      break;
    case 'error':
      console.error(...args);
      break;
  }
}

export const logger = {
  debug(tag: string, payload?: unknown) {
    emit('debug', tag, payload);
  },
  info(tag: string, payload?: unknown) {
    emit('info', tag, payload);
  },
  warn(tag: string, payload?: unknown) {
    emit('warn', tag, payload, true);
  },
  error(tag: string, payload?: unknown) {
    emit('error', tag, payload, true);
  },
};
